import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Popover
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api, { investorApi } from '../../api/api';
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const InvestorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [investData, setInvestData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', mobileNumber: '', password: '' });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const handleActionMenuOpen = (event, row) => { setActionMenuAnchor(event.currentTarget); setSelectedRow(row); };
  const handleActionMenuClose = () => { setActionMenuAnchor(null); setSelectedRow(null); };

  useEffect(() => { fetchInvestData(); }, []);

  const fetchInvestData = async () => {
    setLoading(true);
    try {
      const response = await investorApi.getAllUsers();
      if (Array.isArray(response.data)) setInvestData(response.data);
      else if (Array.isArray(response.data?.data)) setInvestData(response.data.data);
      else setInvestData([]);
    } catch { setInvestData([]); }
    finally { setLoading(false); }
  };

  function capitalizeName(name) {
    if (!name) return '';
    return name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) password += charset.charAt(Math.floor(Math.random() * charset.length));
    setFormData(prev => ({ ...prev, password }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.mobileNumber.trim()) newErrors.mobileNumber = 'Mobile number is required';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddUserSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const userData = { firstName: formData.firstName.trim(), lastName: formData.lastName.trim(), email: formData.email.trim(), mobileNumber: formData.mobileNumber.trim(), password: formData.password };
      const response = await investorApi.addUser(userData);
      if (response.data && !response.data.error) {
        setAddUserModalOpen(false); resetForm(); await fetchInvestData(); showSnackbar('User added successfully!', 'success');
      } else throw new Error(response.data?.message || 'Failed to add user');
    } catch (error) {
      if (error.response?.data?.error) {
        const backendError = error.response.data;
        if (backendError.field) setErrors(prev => ({ ...prev, [backendError.field]: backendError.message }));
        else showSnackbar(backendError.message || 'Error adding user', 'error');
      } else showSnackbar(error.response?.data?.message || 'Error adding user. Please try again.', 'error');
    } finally { setLoading(false); }
  };

  const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

  const getDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));
  const resetForm = () => { setFormData({ firstName: '', lastName: '', email: '', mobileNumber: '', password: '' }); setErrors({}); setShowPassword(false); };
  const handleAddUser = () => { setAddUserModalOpen(true); generatePassword(); };
  const handleCloseModal = () => { setAddUserModalOpen(false); resetForm(); };
  const handleEditUser = (user) => { handleActionMenuClose(); navigate("/user/changeUserDetals", { state: { UserData: user } }); };
  const handleChangePassword = (user) => { handleActionMenuClose(); navigate("/user/changePassword", { state: { userId: user.id } }); };

  const handleExport = async () => {
    try {
      const response = await api.exportUsers();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", "users.xlsx");
      document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { console.error("Export failed", error); }
  };

  const formattedData = Array.isArray(investData)
    ? investData.map(item => ({
        id: item.ID || item.id,
        InvestorName: capitalizeName(item.Username || `${item.FirstName} ${item.LastName}`),
        PhoneNumber: item.Mobilenumber || item.mobileNumber,
        Email: item.Email || item.email,
        JoinedOn: item.CreatedOn || item.createdOn,
        Subscription: item.isSubscribed || item.subscription,
        Avatar: item.avatar?.trim(),
        dateLabel: getDateLabel(item.CreatedOn || item.createdOn),
        refferal_code: item.refferal_code,
      })).sort((a, b) => new Date(b.JoinedOn) - new Date(a.JoinedOn))
    : [];

  const filteredData = formattedData.filter((row) => {
     const matchesSearch = `${row.InvestorName} ${row.Email} ${row.PhoneNumber} ${row.refferal_code || ''}`.toLowerCase().includes(searchQuery.toLowerCase());
    const joinedDate = row.JoinedOn ? new Date(row.JoinedOn) : null;
    const matchesFromDate = fromDate ? joinedDate && joinedDate >= new Date(fromDate) : true;
    const matchesToDate = toDate ? joinedDate && joinedDate <= new Date(toDate) : true;
    return matchesSearch && matchesFromDate && matchesToDate;
  });

  const groupedRows = [];
  let lastDateLabel = null;
  filteredData.forEach((row) => {
    if (row.dateLabel !== lastDateLabel) {
      groupedRows.push({ id: `header-${row.dateLabel}`, isHeader: true, dateLabel: row.dateLabel });
      lastDateLabel = row.dateLabel;
    }
    groupedRows.push(row);
  });

  // ── Columns using flex (no fixed width) so they fill the grid without overflow ──
  const columns = [
    {
      field: 'id',
      headerName: '#',
      width: 55,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isHeader) return '';
        return params.api.getRowIndexRelativeToVisibleRows(params.id);
      },
    },
    {
      field: 'InvestorName',
      headerName: 'User Name',
      flex: 1.8,
      minWidth: 160,
      renderCell: (params) => {
        if (params.row.isHeader) {
          return (
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1976d3' }}>
              {params.row.dateLabel}
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={params.row.Avatar} sx={{ width: 30, height: 30 }} />
            <Typography variant="body2" noWrap>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'PhoneNumber',
      headerName: 'Contact',
      flex: 1,
      minWidth: 120,
      renderCell: params => (
        <Typography variant="body2" sx={{ color: '#0871c5' }} noWrap>{params.value}</Typography>
      ),
    },
    {
      field: 'Email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 150,
      renderCell: params => (
        <Typography variant="body2" sx={{ color: '#0871c5' }} noWrap>{params.value}</Typography>
      ),
    },
    {
      field: 'JoinedOn',
      headerName: 'Joined On',
      flex: 1,
      minWidth: 120,
      renderCell: params => (
        <Typography variant="body2" noWrap>
          {params.value
            ? new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : ''}
        </Typography>
      ),
    },
    {
      field: 'Subscription',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 90,
      renderCell: params => (
        <Typography variant="body2" sx={{ color: '#0871c5', fontWeight: 500 }}>
          {typeof params.value === 'boolean' ? (params.value ? 'Active' : 'Inactive') : params.value}
        </Typography>
      ),
    },
    {
      field: 'refferal_code',
      headerName: 'Referral Code',
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.isHeader) return null;
        return (
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowDropDownIcon />}
            onClick={(event) => handleActionMenuOpen(event, params.row)}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.dark', backgroundColor: 'primary.light' },
            }}
          >
            Actions
          </Button>
        );
      },
    },
  ];

  return (
    // ── overflow: hidden on the outer wrapper prevents any bleed-out scroll ──
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ width: '100%', maxWidth: 1200, minWidth: 0 }}>

        {/* ── Header row ── */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ color: '#13216e', fontWeight: 600, fontSize: 20 }}>Users</Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '15px', color: '#4e0dff' }}>
                Total Users: {filteredData.length}
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser}
                sx={{ backgroundColor: '#1976d3', '&:hover': { backgroundColor: '#1565c0' }, textTransform: 'none' }}>
                Add User
              </Button>
              <Button variant="outlined" onClick={handleExport} sx={{ textTransform: 'none' }}>
                Export Excel
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* ── Search / Filter bar ── */}
        <Paper sx={{ my: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Search by Name, Email, Phone or Referral Code"
                variant="outlined" fullWidth size="small"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="From Date" type="date" InputLabelProps={{ shrink: true }}
                value={fromDate} onChange={(e) => setFromDate(e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="To Date" type="date" InputLabelProps={{ shrink: true }}
                value={toDate} onChange={(e) => setToDate(e.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" fullWidth size="small" sx={{ textTransform: 'none' }}
                onClick={() => { setFromDate(''); setToDate(''); setSearchQuery(''); }}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* ── DataGrid — key fix: width:100%, no inner scroll leaking out ── */}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <DataGrid
            rows={groupedRows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: 2,
              fontSize: '0.875rem',
              backgroundColor: '#ffffff',
              // ── prevent the grid itself from creating a horizontal scrollbar ──
              '& .MuiDataGrid-main': { overflow: 'hidden' },
              '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f6ff' },
              '& .MuiDataGrid-cell': { color: '#333', borderBottom: '1px solid #ddd' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f0f0f0', color: 'black', fontWeight: 'bold' },
              '& .MuiTablePagination-root': { fontSize: '0.875rem' },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
            }}
          />
        </Box>
      </Box>

      {/* ── Actions dropdown ── */}
      <Popover
        open={Boolean(actionMenuAnchor)}
        anchorEl={actionMenuAnchor}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 180 }}>
          {selectedRow && (
            <>
              <MenuItem onClick={() => handleEditUser(selectedRow)} sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 1 }}>
                <EditIcon fontSize="small" sx={{ color: 'primary.main' }} /> Edit User
              </MenuItem>
              <MenuItem onClick={() => handleChangePassword(selectedRow)} sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 1 }}>
                <LockResetIcon fontSize="small" sx={{ color: 'primary.main' }} /> Change Password
              </MenuItem>
            </>
          )}
        </Box>
      </Popover>

      {/* ── Add User Modal ── */}
      <Dialog open={addUserModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d3', color: 'white' }}>
          <Typography variant="h6">Add New User</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name *" value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName} helperText={errors.firstName} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name" value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName} helperText={errors.lastName} margin="normal" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email *" type="email" value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email} helperText={errors.email} margin="normal" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Mobile Number *" value={formData.mobileNumber}
                onChange={(e) => handleInputChange('mobileNumber', e.target.value.replace(/\D/g, ''))}
                error={!!errors.mobileNumber} helperText={errors.mobileNumber}
                margin="normal" inputProps={{ maxLength: 10 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Password *" type={showPassword ? 'text' : 'password'}
                value={formData.password} onChange={(e) => handleInputChange('password', e.target.value)}
                error={!!errors.password} helperText={errors.password} margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <Tooltip title="Generate Password">
                        <IconButton onClick={generatePassword} edge="end" color="primary" sx={{ ml: 1 }}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }} />
              <Typography variant="caption" color="textSecondary">
                Click the refresh icon to generate a new password
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary" disabled={loading}>Cancel</Button>
          <Button onClick={handleAddUserSubmit} variant="contained" disabled={loading}
            sx={{ backgroundColor: '#1976d3', '&:hover': { backgroundColor: '#1565c0' } }}>
            {loading ? 'Adding...' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvestorPage;
